import React, {useRef} from 'react';
import {Image, Point} from '../../admin/artisticobjects/types/Image';
import {useTranslation} from 'react-i18next';
import {getImageUrl} from '../../common/utils/imageUtils';

interface FocalPointSelectorProps {
  image: Image;
  focalPoint?: Point;
  onFocalPointChange: (point: Point) => void;
  onReset: () => void;
}

const FocalPointSelector: React.FC<FocalPointSelectorProps> = ({
                                                                 image,
                                                                 focalPoint,
                                                                 onFocalPointChange,
                                                                 onReset,
                                                               }) => {
  const {t} = useTranslation();
  const imageRef = useRef<HTMLImageElement>(null);

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Map display coordinates to original image coordinates
    const scaleX = image.width / rect.width;
    const scaleY = image.height / rect.height;

    const originalX = Math.round(x * scaleX);
    const originalY = Math.round(y * scaleY);

    onFocalPointChange({x: originalX, y: originalY});
  };

  // Calculate position of the marker as percentages of original image dimensions
  const markerStyle: React.CSSProperties = focalPoint
    ? {
      left: `${(focalPoint.x / image.width) * 100}%`,
      top: `${(focalPoint.y / image.height) * 100}%`,
    }
    : {display: 'none'};

  return (
    <div className="flex flex-col gap-3 items-center">
      <div className="text-sm text-gray-500 w-full text-left">
        {t('admin.common.images.focalPointInstructions', 'Click on the image to set the focal point.')}
      </div>
      <div
        className="focal-point-selector border border-gray-200 rounded-sm overflow-hidden"
        onClick={handleImageClick}
      >
        <img
          ref={imageRef}
          src={getImageUrl(image.path)}
          alt={image.originalName}
          className="focal-point-image pointer-events-none"
        />
        <div className="focal-point-marker" style={markerStyle}/>
      </div>
      {focalPoint && (
        <div className="flex items-center justify-between w-full">
          <span className="text-xs text-gray-400">
            x: {focalPoint.x}, y: {focalPoint.y}
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onReset();
            }}
            className="text-xs text-gray-500 hover:text-gray-800 underline underline-offset-2 transition-colors"
          >
            {t('admin.common.images.resetFocalPoint', 'Reset to calculated')}
          </button>
        </div>
      )}
    </div>
  );
};

export default FocalPointSelector;
